import { type ReactElement, type Ref } from 'react';

import { Button } from '@/ui/components/Button';
import { Icon } from '@/ui/components/Icon';
import type { IconName } from '@/ui/components/iconName';

import type { ProblemRow as ProblemRowModel, SeverityGlyph } from './problemList';
import styles from './ProblemsPanel.module.css';

/**
 * One diagnostic — ADR-0101 §7.14, docs/DESIGN.md §6.4, §7.2, §7.3, §7.5, ADR-0008.
 *
 * ═══ WHAT ADR-0101 CHANGED, AND WHY ═══
 *
 * ADR-0100's row was a five-column grid `18 | 54 | minmax(0,1fr) | auto | 62` on a fixed 30px
 * line — a compiler-output row, and a good one to read twenty of on a 1280px screen. It is the
 * wrong row for a phone, which is the screen this product is now designed for first: 30px is
 * not a touch target, a one-line message ellipsises to uselessness where there is no hover and
 * no `title` tooltip, and a 62px position column holds a sixth of a 375px screen hostage.
 *
 * So the row is two LINES instead of five columns: the message, then the severity word, the
 * position and the rule id under it. Nothing is lost — every column is still on screen, the
 * position is still tabular, the rule id is still greppable (it simply waits for 600px) — and
 * the row is now 50-odd pixels tall, which is a target a thumb can hit.
 *
 * ═══ THE MARKUP IS A BUTTON IN AN `<li>`, WITH NO ROLE OF ITS OWN ═══
 *
 * `<ul role="list"><li><button>`. The button gets neither `role="listitem"` nor
 * `role="option"`: an overlapping ARIA role destroys the native button semantics that are the
 * only reason the row is operable from the keyboard at all (docs/DESIGN.md §6.4). The `<li>` is
 * the list item; the button is the control inside it. `aria-current="true"` marks the selection
 * (§7.5), and the roving `tabIndex` means the list is ONE tab stop rather than twenty.
 *
 * ═══ FIX IS NOW ON EVERY FIXABLE ROW ═══
 *
 * It used to be on the current row only, and the argument was ADR-0100 §7.3: a 28px control on
 * every one of twenty 30px rows is a dense row of targets, which that section forbids. Both
 * halves of that argument are gone. The control is 44px below 960px and the row is 50, so it is
 * not dense; and fixing straight from the list without first selecting the row is the phone use
 * case the whole redesign is for — two taps became one.
 *
 * The TAB-STOP BUDGET is kept by other means: only the current row's Fix is `tabIndex={0}`, so
 * the whole region is still at most two stops and `e2e/a11y.spec.ts` still counts two. A Fix
 * with `tabIndex={-1}` is still clickable, still has its accessible name, and is still reachable
 * from the keyboard — by arrowing to its row first, which is the gesture the list already
 * teaches.
 *
 * It remains a SIBLING of the row button inside the `<li>`, never a descendant: a `<button>` in
 * a `<button>` is invalid HTML that browsers repair by unnesting, taking the inner control's
 * events with it.
 */

export interface ProblemRowProps {
  readonly row: ProblemRowModel;
  /** `aria-current="true"`, the hue edge and wash, the path line, and the tabbable Fix. */
  readonly current: boolean;
  /** Roving focus: exactly one row in the list is `0`, every other is `-1`. */
  readonly tabIndex: number;
  /** The severity WORD, already localised — `Error` / `Warning` / `Note`. */
  readonly severityLabel: string;
  /** `29:7`, already localised through `editor.problems.position`. */
  readonly positionLabel: string;
  /** Localised `action.fix`. Omitted ⇒ no fix affordance, whatever the row carries. */
  readonly fixLabel?: string;
  /** The row became the current one (an arrow key, or the first half of a click). */
  readonly onSelect: () => void;
  /** Jump to the position in the editor (`⏎`, or a click). */
  readonly onActivate: () => void;
  /** Apply `row.fix`. Omitted ⇒ the fix button is not rendered. */
  readonly onApplyFix?: (() => void) | undefined;
  readonly ref?: Ref<HTMLButtonElement>;
}

/**
 * Shape first (docs/DESIGN.md §6.4): a filled circle for an error, a triangle for a warning, a
 * ring for a note. They are drawn glyphs now rather than CSS primitives, because the disc they
 * sit in is 30px and a 7px border trick reads as a speck at that size.
 */
const GLYPH_ICON: Readonly<Record<SeverityGlyph, IconName>> = {
  dot: 'error',
  triangle: 'warning',
  ring: 'info',
};

export function ProblemRow({
  row,
  current,
  tabIndex,
  severityLabel,
  positionLabel,
  fixLabel,
  onSelect,
  onActivate,
  onApplyFix,
  ref,
}: ProblemRowProps): ReactElement {
  const showFix = row.fix !== undefined && onApplyFix !== undefined && fixLabel !== undefined;

  return (
    <li
      className={current ? `${styles.item} ${styles.current}` : styles.item}
      // The hue family the whole row speaks in, set once on the `<li>` as custom properties so
      // the disc, the wash and the edge cannot disagree about which severity this is.
      data-sev={row.severity}
    >
      <button
        aria-current={current ? true : undefined}
        className={styles.diag}
        data-code={row.code}
        onClick={() => {
          onSelect();
          onActivate();
        }}
        ref={ref}
        tabIndex={tabIndex}
        // The full sentence, for a pointer user hovering a row whose message wrapped off the
        // bottom of a short sheet. It is the same string the row shows, never an alternative
        // wording.
        title={row.plainMessage}
        type="button"
      >
        <span aria-hidden="true" className={styles.sev}>
          <Icon name={GLYPH_ICON[row.glyph]} />
        </span>
        <span className={styles.main}>
          <span className={styles.msg}>
            {row.message.map((segment, index) =>
              segment.kind === 'code' ? (
                // The index is a legitimate key here and nowhere else: segments are derived
                // deterministically from one template plus one params object, they are never
                // reordered, inserted into or removed from, and the whole list is replaced
                // whenever the diagnostic changes. `messageSegments` splits on the TEMPLATE's
                // placeholders rather than by parsing prose, so a translator reordering a
                // sentence cannot lose a token either.
                <code key={index}>{segment.text}</code>
              ) : (
                <span key={index}>{segment.text}</span>
              ),
            )}
          </span>
          {/*
            The second line: the WORD channel, then the position, then the machine-readable
            rule id. All three are real text at every width — the rule id is merely not painted
            below 600px, where the row has no room for `syntax/trailing-comma` and the word and
            the position are what a phone user is actually looking for.
          */}
          <span className={styles.where}>
            <span className={styles.word}>{severityLabel}</span>
            <span className={styles.pos}>{positionLabel}</span>
            <span className={styles.rule}>{row.code}</span>
          </span>
          {/* Structural location beats coordinates, but only the current row pays for it with
              height. Rendered by the component, so an unselected row emits no dead node. */}
          {current && row.path !== undefined ? (
            <span className={styles.path}>{row.path}</span>
          ) : null}
        </span>
      </button>
      {showFix ? (
        <Button
          // The label comes from the engine's own `fix.*` template, so the user is told WHAT
          // will change before they consent to it (ADR-0012).
          aria-label={`${fixLabel}: ${row.fix?.label ?? ''}`}
          className={styles.fix}
          // A heuristic fix is a guess about intent (ADR-0012). It is offered, never applied in
          // bulk, and it says so in HUE — amber is "changed but valid" — rather than only in a
          // tooltip. `Fix all` never applies one; this button is the only way one is ever
          // applied.
          data-confidence={row.fix?.confidence}
          hue={row.fix?.confidence === 'heuristic' ? 'amber' : 'lemon'}
          icon="spark"
          label={fixLabel}
          onClick={onApplyFix}
          size="sm"
          // Only the current row's Fix is in the tab order — see the header's note on the
          // two-stop budget.
          tabIndex={current ? 0 : -1}
          title={row.fix?.label}
          variant="tinted"
        />
      ) : null}
    </li>
  );
}

export default ProblemRow;
